import React, { useState, useEffect, useRef, useContext } from 'react';
import { AuthContext } from '../App';
import { MessageCircle, Send, X, ThumbsUp, ThumbsDown, Bot, User, Minimize2, Maximize2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function CustomerSupportChat() {
  const { user } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    // Generate session ID on component mount
    setSessionId(`chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
    
    // Listen for open chatbot events from navbar
    const handleOpenChatbot = () => {
      openChat();
    };
    
    window.addEventListener('openChatbot', handleOpenChatbot);
    
    return () => {
      window.removeEventListener('openChatbot', handleOpenChatbot);
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!currentMessage.trim() || isTyping) return;

    const userMessage = {
      id: `user_${Date.now()}`,
      text: currentMessage.trim(),
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setIsTyping(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: userMessage.text,
        session_id: sessionId
      });

      const aiMessage = {
        id: response.data.message_id,
        text: response.data.response,
        sender: 'ai',
        timestamp: new Date(),
        session_id: response.data.session_id
      };

      setMessages(prev => [...prev, aiMessage]);
      
      // Update session ID if it was generated by backend
      if (response.data.session_id !== sessionId) {
        setSessionId(response.data.session_id);
      }
      
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = {
        id: `error_${Date.now()}`,
        text: 'Sorry, I\'m having trouble connecting right now. Please try again in a moment.',
        sender: 'ai',
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const provideFeedback = async (messageId, isHelpful) => {
    try {
      await axios.post(`${API}/chat/feedback`, {
        message_id: messageId,
        is_helpful: isHelpful
      });

      // Update message to show feedback was given
      setMessages(prev => prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, feedbackGiven: true, wasHelpful: isHelpful }
          : msg
      ));
    } catch (error) {
      console.error('Feedback error:', error);
    }
  };

  const openChat = () => {
    setIsOpen(true);
    setIsMinimized(false);
    
    // Add welcome message if first time opening
    if (messages.length === 0) {
      const welcomeMessage = {
        id: 'welcome',
        text: `👋 Hi${user ? ` ${user.full_name}` : ''}! I'm ZypeeTravel's AI Customer Service Assistant. I can help you with:\n\n🏨 Booking questions\n💰 Pricing and plans\n📋 Cancellation policies\n🏆 Loyalty program\n⚙️ Account management\n\nHow can I assist you today?`,
        sender: 'ai',
        timestamp: new Date()
      };
      setMessages([welcomeMessage]);
    }
  };

  const closeChat = () => {
    setIsOpen(false);
    setIsMinimized(false);
  };

  const toggleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  if (!isOpen) {
    return (
      <div 
        className="customer-support-fab"
        onClick={openChat}
        style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          width: '70px',
          height: '70px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #10B981, #059669)',
          color: 'white',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 8px 32px rgba(16, 185, 129, 0.4)',
          transition: 'all 0.3s ease',
          zIndex: 1000,
          animation: 'pulse 2s infinite'
        }}
      >
        <MessageCircle size={32} />
        
        {/* Notification badge */}
        <div style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: '#ef4444',
          color: 'white',
          fontSize: '12px',
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          ?
        </div>
        
        {/* Help text tooltip */}
        <div style={{
          position: 'absolute',
          right: '80px',
          top: '50%',
          transform: 'translateY(-50%)',
          background: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '14px',
          whiteSpace: 'nowrap',
          opacity: '0',
          animation: 'fadeInOut 4s infinite'
        }}>
          Need help? Click to chat!
        </div>
        
        <style jsx>{`
          @keyframes pulse {
            0% { 
              box-shadow: 0 8px 32px rgba(16, 185, 129, 0.4);
              transform: scale(1);
            }
            50% { 
              box-shadow: 0 8px 40px rgba(16, 185, 129, 0.6);
              transform: scale(1.05);
            }
            100% { 
              box-shadow: 0 8px 32px rgba(16, 185, 129, 0.4);
              transform: scale(1);
            }
          }
          
          @keyframes fadeInOut {
            0%, 70%, 100% { opacity: 0; }
            10%, 60% { opacity: 1; }
          }
          
          .customer-support-fab:hover {
            transform: scale(1.1) !important;
            box-shadow: 0 12px 40px rgba(16, 185, 129, 0.5) !important;
          }
          
          .customer-support-fab:hover + div {
            opacity: 1 !important;
          }
        `}</style>
      </div>
    );
  }

  return (
    <div 
      className="customer-support-chat"
      style={{
        position: 'fixed',
        bottom: '24px',
        right: '24px',
        width: '400px',
        height: isMinimized ? '60px' : '600px',
        background: 'white',
        borderRadius: '16px',
        boxShadow: '0 16px 48px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e2e8f0',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1000,
        transition: 'all 0.3s ease',
        overflow: 'hidden'
      }}
    >
      {/* Chat Header */}
      <div 
        style={{
          background: 'linear-gradient(135deg, #10B981, #059669)',
          color: 'white',
          padding: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: isMinimized ? 'pointer' : 'default'
        }}
        onClick={isMinimized ? toggleMinimize : undefined}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div 
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <Bot size={20} />
          </div>
          <div>
            <div style={{ fontWeight: '600', fontSize: '1rem' }}>ZypeeTravel Support</div>
            <div style={{ fontSize: '0.8rem', opacity: '0.9' }}>
              {isTyping ? 'AI Assistant is typing...' : 'AI-powered customer service'}
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={toggleMinimize}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {isMinimized ? <Maximize2 size={18} /> : <Minimize2 size={18} />}
          </button>
          <button
            onClick={closeChat}
            style={{
              background: 'none',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {!isMinimized && (
        <>
          {/* Chat Messages */}
          <div 
            style={{
              flex: 1,
              padding: '16px',
              overflowY: 'auto',
              background: '#f8fafc'
            }}
          >
            {messages.map((message) => (
              <div
                key={message.id}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  marginBottom: '16px',
                  flexDirection: message.sender === 'user' ? 'row-reverse' : 'row'
                }}
              >
                {/* Avatar */}
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: message.sender === 'user' 
                      ? 'linear-gradient(135deg, #667eea, #764ba2)' 
                      : 'linear-gradient(135deg, #10B981, #059669)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontSize: '14px',
                    flexShrink: 0
                  }}
                >
                  {message.sender === 'user' ? <User size={16} /> : <Bot size={16} />}
                </div>

                {/* Message Bubble */}
                <div style={{ maxWidth: '75%', minWidth: '120px' }}>
                  <div
                    style={{
                      padding: '12px 16px',
                      borderRadius: '18px',
                      background: message.sender === 'user' 
                        ? '#667eea' 
                        : message.isError 
                          ? '#fee2e2'
                          : 'white',
                      color: message.sender === 'user' 
                        ? 'white' 
                        : message.isError
                          ? '#dc2626'
                          : '#2d3748',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      whiteSpace: 'pre-wrap',
                      lineHeight: '1.5'
                    }}
                  >
                    {message.text}
                  </div>
                  
                  {/* Feedback buttons for AI messages */}
                  {message.sender === 'ai' && !message.isError && !message.feedbackGiven && message.id !== 'welcome' && (
                    <div 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '8px',
                        fontSize: '0.8rem',
                        color: '#6b7280'
                      }}
                    >
                      <span>Was this helpful?</span>
                      <button
                        onClick={() => provideFeedback(message.id, true)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#10b981',
                          padding: '4px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <ThumbsUp size={14} />
                      </button>
                      <button
                        onClick={() => provideFeedback(message.id, false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#ef4444',
                          padding: '4px',
                          borderRadius: '4px',
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <ThumbsDown size={14} />
                      </button>
                    </div>
                  )}

                  {/* Feedback confirmation */}
                  {message.feedbackGiven && (
                    <div 
                      style={{
                        fontSize: '0.75rem',
                        color: '#6b7280',
                        marginTop: '4px',
                        fontStyle: 'italic'
                      }}
                    >
                      ✓ Thanks for your feedback!
                    </div>
                  )}

                  {/* Timestamp */}
                  <div 
                    style={{
                      fontSize: '0.7rem',
                      color: '#9ca3af',
                      marginTop: '4px',
                      textAlign: message.sender === 'user' ? 'right' : 'left'
                    }}
                  >
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            
            {/* Typing indicator */}
            {isTyping && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '16px'
                }}
              >
                <div
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10B981, #059669)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white'
                  }}
                >
                  <Bot size={16} />
                </div>
                <div 
                  style={{
                    padding: '12px 16px',
                    borderRadius: '18px',
                    background: 'white',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  <div className="typing-dot" style={{ 
                    width: '6px', 
                    height: '6px', 
                    borderRadius: '50%', 
                    background: '#10B981',
                    animation: 'typing 1.4s infinite ease-in-out'
                  }}></div>
                  <div className="typing-dot" style={{ 
                    width: '6px', 
                    height: '6px', 
                    borderRadius: '50%', 
                    background: '#10B981',
                    animation: 'typing 1.4s infinite ease-in-out 0.2s'
                  }}></div>
                  <div className="typing-dot" style={{ 
                    width: '6px', 
                    height: '6px', 
                    borderRadius: '50%', 
                    background: '#10B981',
                    animation: 'typing 1.4s infinite ease-in-out 0.4s'
                  }}></div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Message Input */}
          <form 
            onSubmit={sendMessage}
            style={{
              padding: '16px',
              background: 'white',
              borderTop: '1px solid #e2e8f0',
              display: 'flex',
              gap: '12px',
              alignItems: 'flex-end'
            }}
          >
            <textarea
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage(e);
                }
              }}
              placeholder="Type your message here..."
              rows="1"
              style={{
                flex: 1,
                padding: '12px',
                borderRadius: '12px',
                border: '1px solid #d1d5db',
                resize: 'none',
                fontSize: '0.9rem',
                minHeight: '20px',
                maxHeight: '80px',
                outline: 'none',
                fontFamily: 'inherit'
              }}
              disabled={isTyping}
            />
            <button
              type="submit"
              disabled={!currentMessage.trim() || isTyping}
              style={{
                padding: '10px',
                borderRadius: '50%',
                border: 'none',
                background: (!currentMessage.trim() || isTyping) 
                  ? '#d1d5db' 
                  : 'linear-gradient(135deg, #10B981, #059669)',
                color: 'white',
                cursor: (!currentMessage.trim() || isTyping) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <Send size={16} />
            </button>
          </form>
        </>
      )}

      <style jsx>{`
        @keyframes typing {
          0%, 60%, 100% { transform: translateY(0); opacity: 0.3; }
          30% { transform: translateY(-10px); opacity: 1; }
        }
      `}</style>
    </div>
  );
}

export default CustomerSupportChat;